import { createResource, deleteResource, getResource } from './api';

// Video-pitch API: sign, attach, detach. All three route to V2 through the bridge (resolveApiUrl):
// same-origin in V2, cross-origin via V2_URL + JWT in V1. The pitch is addressed by its V1 id,
// which V2 resolves as migrate_id (numeric-params convention).

// Sync normally lands in seconds (staging-verified ~3s), but a Kafka hiccup shouldn't cost
// the promoter their recording — retry for ~54s total (1.5s * (1+..+8)) before giving up.
const ATTACH_RETRIES = 8;
const ATTACH_RETRY_BASE_MS = 1500;

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// The wizard must never hard-redirect on auth failure (the bridge default) — a mid-pitch user
// losing their draft to a dashboard bounce is worse than a missing video. With the redirect off,
// fetchWithTokenRefresh throws instead, so failures collapse to the same { error } shape the
// section already renders as an inline error/empty state.
const NO_REDIRECT = { redirectOnAuthFailure: false };
const asError = (e) => ({ error: { status: 0, statusText: (e && e.message) || 'request failed', body: null } });

export const signPitchVideoUpload = async () => createResource('/interact/api/pitching/video_signature', {}, NO_REDIRECT).catch(asError);

// The pitch's current video (draft-reopen rediscovery). 404 => no video, the composer empty state.
export const getPitchVideo = async (pitchV1Id) => getResource(`/interact/api/pitching/pitches/${pitchV1Id}/video`, NO_REDIRECT).catch(asError);

// Attach an uploaded video reference to the pitch (pitchV1Id = V2 migrate_id). If the attach
// beats the V1->V2 draft sync, V2 answers 404 and we retry with backoff — the race contract.
export const attachPitchVideo = async (pitchV1Id, reference) => {
  let result = null;
  for (let attempt = 0; attempt <= ATTACH_RETRIES; attempt += 1) {
    if (attempt > 0) {
      // eslint-disable-next-line no-await-in-loop -- retries are sequential by definition
      await sleep(ATTACH_RETRY_BASE_MS * attempt);
    }
    // eslint-disable-next-line no-await-in-loop -- retries are sequential by definition
    result = await createResource(`/interact/api/pitching/pitches/${pitchV1Id}/video`, {
      data: { attributes: reference },
    }, NO_REDIRECT).catch(asError);
    if (!result.error || result.error.status !== 404) {
      return result;
    }
  }
  return result;
};

export const deletePitchVideo = async (attachmentId) => deleteResource(`/interact/api/pitching/videos/${attachmentId}`, NO_REDIRECT).catch(asError);

export default {
  signPitchVideoUpload, getPitchVideo, attachPitchVideo, deletePitchVideo,
};
